import Info from '@mui/icons-material/Info';
import MuiButton from '@mui/material/Button';
import { Box, Icon, Typography } from '@qbe/ads-core';
import React, { useState } from 'react';

import { useHelperText } from '~/components-ds/HelperTextProvider/useHelperText';
import { imgMap } from '~/components-ds/Icon/Icon';
import { useAppContent, useAppDispatch } from '~/hooks';
import { qalTrackEngagementAction } from '~/thunks/analytics';
import { analyticsConstant } from '~/types/analytics';
import { Cover } from '~/types/cover';
import { typeToImageCovers } from '~/utils';

import { CoverInclusionsModal } from './CoverInclusionsModal';

type CoverOptionRadioProps = {
  isSelected: boolean;
  id: string;
  disabled: boolean;
};

const CoverOptionRadio = ({ isSelected, id, disabled }: CoverOptionRadioProps) => (
  <Box
    component="input"
    type="radio"
    checked={isSelected}
    data-track={id}
    data-track-value="capture"
    disabled={id === Cover.Liability || disabled}
    readOnly
    sx={{
      appearance: 'none',
      height: '40px',
      width: '40px',
      margin: '0',
      backgroundImage: `url(${imgMap.radioUnselectedLg})`,
      backgroundSize: 'cover',
      '&:checked': {
        backgroundImage: `url(${imgMap.radioSelectedLg})`,
      },
      '&:checked:disabled': {
        backgroundImage: `url(${imgMap.radioPreSelectedLg})`,
        cursor: 'default',
      },
      '&:disabled': {
        cursor: 'default',
      },
    }}
  />
);

type CoverOptionTextProps = {
  title: React.ReactNode;
  description: React.ReactNode;
  onClickHelp: (e: React.MouseEvent<HTMLSpanElement, MouseEvent>) => void;
};

const CoverOptionText = ({ title, description, onClickHelp }: CoverOptionTextProps) => (
  <>
    <Typography variant="h5" component="span" fontSize="20px" lineHeight="28px" display="block">
      {title}
      <Typography
        component="span"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onClickHelp(e);
        }}
      >
        <Icon
          data-track="info"
          muiIcon={Info}
          color="white"
          size={24}
          sx={{
            marginLeft: '4px',
            verticalAlign: 'middle',
            marginBottom: '7px',
          }}
        />
      </Typography>
    </Typography>

    <Typography
      variant="subtitle2"
      component="span"
      fontSize="12px"
      lineHeight="18px"
      sx={{ display: 'block' }}
    >
      {description}
    </Typography>
  </>
);

interface CoverOptionProps {
  id: Cover;
  isSelected: boolean;
  handleSelect: (id: Cover) => void;
  disabled: boolean;
}

export const CoverOption = ({ id, isSelected, handleSelect, disabled }: CoverOptionProps) => {
  const dispatch = useAppDispatch();
  const { selectUiCover } = useAppContent();

  const uiCover = selectUiCover(id);
  const { openHelper } = useHelperText();

  const [showModal, setShowModal] = useState(false);

  const { getTextContent } = useAppContent({
    basePath: 'dsme.coverOptionComponent',
  });

  if (!uiCover) return null;

  return (
    <>
      <MuiButton
        key={id}
        data-track={id}
        data-track-value="capture"
        onClick={() => {
          if (!disabled) {
            handleSelect(id);
          }
        }}
        aria-disabled={disabled}
        sx={{
          color: 'white',
          textTransform: 'unset',
          display: 'block',
          textAlign: 'left',
          height: '220px',
          width: '100%',
          padding: '1em',
          lineHeight: 1,
          // start interactive styles
          backgroundImage: isSelected
            ? `linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(26, 26, 26, 0.94) 100%), url(${typeToImageCovers[id]})`
            : `url(${typeToImageCovers[id]})`,
          backgroundSize: 'cover',
          cursor: 'pointer',
          '&:hover': {
            backgroundImage: `linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(26, 26, 26, 0.94) 100%), url(${typeToImageCovers[id]})`,
          },
          "&[aria-disabled='true']": {
            backgroundImage: isSelected
              ? `linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(26, 26, 26, 0.94) 100%), url(${typeToImageCovers[id]})`
              : `url(${typeToImageCovers[id]})`,
            cursor: 'default',
            opacity: !isSelected ? 0.3 : undefined,
            // end interactive styles
          },
        }}
      >
        <Box display="flex" flexDirection="column" height="100%">
          <Box flex={1}>
            <CoverOptionRadio isSelected={isSelected} id={id} disabled={disabled} />
          </Box>
          <Box>
            <CoverOptionText
              title={uiCover.name}
              description={uiCover.description}
              onClickHelp={(e) => {
                if (id === Cover.BusinessInterruption && disabled) {
                  openHelper(
                    getTextContent('contextualhelpers.coverOptionBusinessInterruption.description')
                  )(e);
                  return;
                }
                setShowModal(true);

                dispatch(
                  qalTrackEngagementAction({
                    eventAction: `help: ${id} info`,
                    eventLabel: analyticsConstant.NOT_SET,
                    data: {},
                    nonInteraction: false,
                  })
                );
              }}
            />
          </Box>
        </Box>
      </MuiButton>

      {showModal && (
        <CoverInclusionsModal
          open
          inclusionId={id}
          analyticsModalName={id}
          onClose={() => {
            setShowModal(false);
            dispatch(
              qalTrackEngagementAction({
                eventAction: 'modal: close',
                eventLabel: `${id}`,
                data: {},
              })
            );
          }}
        />
      )}
    </>
  );
};
